/**
 * Zod schema for the "Register Agent" form (F031 Agent Registry, form-spec `FS-F031-AGENT`, A-01) —
 * Doc 69 Layer 2 output, imported by `AgentRegisterForm.tsx`, never redefined
 * (form-standard.context.md §3). Rule IDs `V-01`..`V-18` are the form-spec's own IDs (verbatim from
 * `.specify/features/_active/FEA-F031-agent-registry/forms/AgentRegistration.form-spec.md`) — kept
 * unrenamed so the spec's Conformance Table can point straight at this file. `V-01`/`V-02` (Agent
 * Name) and `V-07`/`V-08` (Owner Id) are create-only per the spec's Field Dictionary (F-01/F-04:
 * "create only, immutable on edit") — they live here, not in `AgentEdit.schema.ts`.
 */
import { z } from 'zod';
import type { AgentEnvironment, AgentRegistrationInput, DataAccessLevel, ToolAccessLevel } from '../../../types';
import { AGENT_ENVIRONMENT_OPTIONS, DATA_ACCESS_LEVEL_OPTIONS, TOOL_ACCESS_LEVEL_OPTIONS } from './agentFormFields';

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

// reason: AGENT_ENVIRONMENT_OPTIONS/DATA_ACCESS_LEVEL_OPTIONS/TOOL_ACCESS_LEVEL_OPTIONS are
// runtime-verified non-empty arrays of the exported union types; zod's z.enum() requires a literal
// non-empty tuple type.
const ENVIRONMENT_OPTIONS = AGENT_ENVIRONMENT_OPTIONS as [AgentEnvironment, ...AgentEnvironment[]];
const DATA_ACCESS_LEVEL_TUPLE = DATA_ACCESS_LEVEL_OPTIONS as [DataAccessLevel, ...DataAccessLevel[]];
const TOOL_ACCESS_LEVEL_TUPLE = TOOL_ACCESS_LEVEL_OPTIONS as [ToolAccessLevel, ...ToolAccessLevel[]];

export const AgentRegisterSchema = z.object({
  // V-01 (required @ submit) / V-02 (max 200 @ blur)
  agentName: z
    .string()
    .trim()
    .min(1, 'Agent name is required')
    .max(200, 'Agent name must be 200 characters or fewer'),
  // V-03 (required @ submit) / V-04 (max 50 @ blur)
  agentVersion: z
    .string()
    .trim()
    .min(1, 'Agent version is required')
    .max(50, 'Agent version must be 50 characters or fewer'),
  // V-05 (required @ submit) / V-06 (max 1000 @ blur)
  purpose: z
    .string()
    .trim()
    .min(1, 'Purpose is required')
    .max(1000, 'Purpose must be 1000 characters or fewer'),
  // V-07 (required, create only @ submit) / V-08 (max 200 @ blur)
  ownerId: z
    .string()
    .trim()
    .min(1, 'Owner id is required')
    .max(200, 'Owner id must be 200 characters or fewer'),
  // V-09 (optional, max 200 @ blur)
  ownerTeam: z
    .string()
    .max(200, 'Owner team must be 200 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // V-10 — 'Development' is itself a valid AgentEnvironment, so the Dropdown's default selection
  // already satisfies this rule in practice (matches the original manual validator's behaviour).
  environment: z.enum(ENVIRONMENT_OPTIONS, { message: 'Select an environment' }),
  // V-11 (required @ submit) / V-12 (max 100 @ blur)
  modelProvider: z
    .string()
    .trim()
    .min(1, 'Model provider is required')
    .max(100, 'Model provider must be 100 characters or fewer'),
  // V-13 (required @ submit) / V-14 (max 200 @ blur)
  modelName: z
    .string()
    .trim()
    .min(1, 'Model name is required')
    .max(200, 'Model name must be 200 characters or fewer'),
  // V-15 (optional, max 100 @ blur)
  runtimeProvider: z
    .string()
    .max(100, 'Runtime provider must be 100 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // V-16
  dataAccessLevel: z.enum(DATA_ACCESS_LEVEL_TUPLE, { message: 'Select a data access level' }),
  // V-17
  toolAccessLevel: z.enum(TOOL_ACCESS_LEVEL_TUPLE, { message: 'Select a tool access level' }),
  mcpEnabled: z.boolean(),
  // F-13 — Capabilities tag input, optional, defaults to [].
  capabilities: z.array(z.string()).transform((v) => (v.length > 0 ? v : undefined)),
  // V-18 (optional, valid JSON when non-empty @ blur)
  metadataJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Metadata must be valid JSON' })
    .transform((v) => v || undefined),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type AgentRegisterFormValues = z.input<typeof AgentRegisterSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed, undefined-if-empty). */
export type AgentRegisterFormOutput = z.output<typeof AgentRegisterSchema>;

// Compile-time proof the schema's output assembles a valid A-01 request DTO.
type _AssertAssignable = AgentRegisterFormOutput extends AgentRegistrationInput ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
